// Tiny typed fetch wrapper. All API calls go through the Vite proxy (/api/*),
// so cookies are same-origin and we never have to think about CORS.
//
// Errors:
//   - fetch network failure → throws ApiError with no `status`
//   - non-2xx → throws ApiError with parsed JSON body if available
//
// Locale: every request includes X-UI-Locale so the chat agent (and any
// other locale-aware route) replies in the user's UI language. The api
// reads this header in /chat and /chat/kickoff, replacing the hardcoded
// `uiLocale: 'en'`.

import { currentLocale } from './locale';

export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export type ApiOptions = {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /** Set false on auth endpoints where 401 is just "not signed in", not an error. */
  throwOn401?: boolean;
};

export async function apiFetch<T = unknown>(path: string, opts: ApiOptions = {}): Promise<T> {
  const { method = 'GET', body, signal, throwOn401 = true } = opts;

  // Always send Content-Type for mutating methods. The api enforces it as
  // a CSRF guard — a Content-Type: application/json request is a non-simple
  // CORS request, so cross-origin POSTs without our preflight are blocked
  // by the browser. Custom X-UI-Locale also forces preflight; belt-and-
  // suspenders.
  const headers: Record<string, string> = { 'X-UI-Locale': currentLocale() };
  if (body !== undefined || method !== 'GET') headers['Content-Type'] = 'application/json';

  const res = await fetch(`/api${path}`, {
    method,
    credentials: 'include',
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  });

  if (!res.ok) {
    if (res.status === 401 && !throwOn401) {
      return null as T;
    }
    let parsed: unknown;
    try {
      parsed = await res.json();
    } catch {
      parsed = await res.text().catch(() => undefined);
    }
    throw new ApiError(`API ${method} ${path} failed: ${res.status}`, res.status, parsed);
  }

  // 204 / empty
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
